import { ProviderAddress } from '../api';
import { Address } from '../api/address.types';

export function getAddressByType(addressMap: { [index: string]: Address }, addressType: string): Address {
  for (const key in addressMap) {
    if (Object.prototype.hasOwnProperty.call(addressMap, key)) {
      const address = addressMap[key];
      if (address.addressType.toLowerCase() === addressType.toLowerCase()) {
        return address;
      }
    }
  }

  return {
    addressType: addressType,
  } as Address;
}

export function getAddressById(addressMap: { [index: string]: Address }, addressId: string): Address | null {
  if (!addressId) {
    return null;
  }

  const addressByLookup = addressMap[addressId];
  if (addressByLookup) {
    return addressByLookup;
  }

  for (const key in addressMap) {
    if (Object.prototype.hasOwnProperty.call(addressMap, key)) {
      const address = addressMap[key];
      if (address.addressId.toLowerCase() === addressId.toLowerCase()) {
        return address;
      }
    }
  }

  return null;
}

export function distanceBetweenLocations(lat1: number, lon1: number, lat2: number, lon2: number) {
  if (lat1 === lat2 && lon1 === lon2) {
    return 0;
  } else {
    const radlat1 = (Math.PI * lat1) / 180;
    const radlat2 = (Math.PI * lat2) / 180;
    const theta = lon1 - lon2;
    const radtheta = (Math.PI * theta) / 180;
    let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
    if (dist > 1) {
      dist = 1;
    }
    dist = Math.acos(dist);
    dist = (dist * 180) / Math.PI;
    dist = dist * 60 * 1.1515;
    return dist;
  }
}

/**
 * Take in an Address or Address-like object and return a string representing that address.
 * @param address An Address, a partial Address, or an Address-like which, in all cases, contains the following keys:
 * - `streetAddress`
 * - `city`
 * - `state`
 * - `zipcode`
 * - `country`
 * @returns A string representing the specified Address, or an empty string if any of the required keys were empty.
 */
export const getFullAddressString = (address: Pick<Address, "streetAddress"|"city"|"state"|"zipcode"|"country">) => {
  const currentAddressValues = [address.streetAddress, address.city, address.state, address.zipcode, address.country];

  // If any of the fields required to assemble the address is undefined or empty (''), return nothing.
  // This will most often occur in practice when the API returns the address as a "Plus Code".
  if (currentAddressValues.some(v => v === undefined || v === '')) return '';

  return `${address.streetAddress}, ${address.city}, ${address.state} ${address.zipcode}, ${address.country}`;
}

/**
 * Take in a ProviderAddress or ProviderAddress-like object and return a string representing that address.
 * @param providerAddress A ProviderAddress, a partial ProviderAddress, or an ProviderAddress-like which, in all cases, contains the following keys:
 * - `addressLine1`
 * - `city`
 * - `state`
 * - `zipcode`
 * - `country`
 * @returns A string representing the specified ProviderAddress, or an empty string if any of the required keys were empty.
 */
export const getFullProviderAddressString = (providerAddress?: Pick<ProviderAddress, "addressLine1"|"city"|"state"|"zipcode"|"country">) => {
  /**
   * Provider addresses in production may sometimes be undefined.
   * @see [PRODOPS-6058](https://jira.heal.com/browse/PRODOPS-6058)
   * @see [APP-9218](https://jira.heal.com/browse/APP-9218)
   */
  if (!providerAddress) return '';
  
  const currentAddressValues = [providerAddress.addressLine1, providerAddress.city, providerAddress.state, providerAddress.zipcode, providerAddress.country];

  // If any of the fields required to assemble the address is undefined or empty (''), return nothing.
  // This will most often occur in practice when the API returns the address as a "Plus Code".
  if (currentAddressValues.some(v => v === undefined || v === '')) return '';

  return `${providerAddress.addressLine1}, ${providerAddress.city}, ${providerAddress.state} ${providerAddress.zipcode}, ${providerAddress.country}`;
}
